"use client";

import { ArrowUpRight, Brain, Cpu, Lock, MousePointer2, Network, Sigma } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const labs = [{
  id: "GEAR-01",
  name: "The Drum Bunker",
  description: "Premium birch and maple kits tuned for maximum impact. From vintage snares to heavy-duty hardware.",
  tags: ["#DW-COLLECTORS", "#ZILDJIAN-K", "#REMO-HEADS"],
  accentVar: "--chart-1",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/studio_drums_gritty.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/studio_drums_gritty.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/studio_drums_gritty.png-wvc-sizes",
  icon: Brain
}, {
  id: "GEAR-02",
  name: "Tube Amp Alley",
  description: "A wall of legendary glass. British crunch, American cleans, and boutique high-gain monsters.",
  tags: ["#MARSHALL-JCM", "#FENDER-TWIN", "#VOX-AC30"],
  accentVar: "--chart-3",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/guitar_amps_tubes.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/guitar_amps_tubes.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/guitar_amps_tubes.png-wvc-sizes",
  icon: Lock
}, {
  id: "GEAR-03",
  name: "The Live Rig",
  description: "High-headroom PA systems and floor monitors designed to handle the loudest rehearsals.",
  tags: ["#QSC-TOUCHMIX", "#SHURE-SM58", "#MACKIE-THUMP"],
  accentVar: "--chart-2",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/live_pa_system.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/live_pa_system.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/live_pa_system.png-wvc-sizes",
  icon: Cpu
}, {
  id: "GEAR-04",
  name: "Bass Foundation",
  description: "Earth-shaking low end provided by 8x10 stacks and modern class-D powerhouses.",
  tags: ["#AMPEG-SVT", "#DARKGLASS", "#ORANGE-AD200"],
  accentVar: "--chart-4",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/bass_rig_stack.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/bass_rig_stack.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/bass_rig_stack.png-wvc-sizes",
  icon: MousePointer2
}, {
  id: "GEAR-05",
  name: "Tracking Station",
  description: "Multi-track recording capabilities to capture your live energy with zero latency.",
  tags: ["#FOCUSRITE", "#LOGIC-PRO", "#NEVE-PREAMPS"],
  accentVar: "--chart-1",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/recording_console.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/recording_console.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/recording_console.png-wvc-sizes",
  icon: Sigma
}, {
  id: "GEAR-06",
  name: "The Pedal Vault",
  description: "A curated collection of fuzz, delay, and modulation to find your signature sonic texture.",
  tags: ["#STRYMON", "#EARTHQUAKER", "#BOSS-VINTAGE"],
  accentVar: "--chart-2",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/pedalboard_setup.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/pedalboard_setup.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/pedalboard_setup.png-wvc-sizes",
  icon: Network
}];
export default function TheArsenal() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="530" id="the-arsenal" className="bg-background text-foreground relative overflow-hidden py-20 sm:py-28 lg:py-36" style={{
    backgroundImage: "radial-gradient(ellipse at center, var(--background) 0%, hsl(220 15% 3%) 100%)"
  }}>
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-12">
            <span className="font-mono text-xs sm:text-sm tracking-[0.2em] uppercase font-medium" style={{
            color: "var(--chart-1)"
          }}>
              SECTION_01 // THE_ARSENAL
            </span>
          </div>
          <div className="lg:col-span-10 lg:col-start-2">
            <h2 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[0.9] tracking-tighter font-extrabold uppercase">
              The Backline.
              <br />
              <span className="text-muted-foreground">Built for the loud.</span>
            </h2>
          </div>
        </div>

        {}
        <div ref={gridRef} className="mt-16 grid grid-cols-1 gap-px bg-border/40 md:grid-cols-2 lg:grid-cols-3">
          {labs.map((lab, i) => {
          const Icon = lab.icon;
          return <article key={lab.id} data-index={i} className={`group relative flex flex-col bg-card border border-border transition-all duration-700 ease-out hover:border-primary md:h-[480px] rounded-none ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms",
            "--lab-accent": `var(${lab.accentVar})`
          }} onMouseEnter={e => {
            e.currentTarget.style.borderColor = `var(${lab.accentVar})`;
          }} onMouseLeave={e => {
            e.currentTarget.style.borderColor = "";
          }}>
                {}
                <div className="relative h-48 sm:h-52 overflow-hidden">
                  <div className="absolute inset-0 transition-all duration-0 ease-out group-hover:opacity-100" style={{
                background: `linear-gradient(135deg, var(${lab.accentVar}) 0%, hsl(220 15% 6%) 100%)`,
                opacity: 0.85
              }} />
                  <img src={lab.image} data-wvc-srcset={lab.srcset} data-wvc-sizes={lab.sizes} alt={lab.name} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover mix-blend-luminosity grayscale opacity-60 transition-all duration-700 ease-out group-hover:grayscale-0 group-hover:opacity-90 group-hover:mix-blend-normal" />
                  <div className="absolute top-4 right-4">
                    <Icon className="h-5 w-5 text-foreground/70 transition-colors duration-0 group-hover:text-foreground" strokeWidth={1.5} />
                  </div>
                </div>

                {}
                <div className="relative flex flex-1 flex-col p-6 sm:p-7">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] tracking-widest text-muted-foreground transition-colors duration-0" style={{}}>
                      [{lab.id}]
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground/60">
                      /{String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="font-default mt-4 text-2xl sm:text-3xl leading-[1.1] tracking-tight font-semibold uppercase">
                    {lab.name}
                  </h3>

                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground font-default">
                    {lab.description}
                  </p>

                  <ul className="mt-5 flex flex-wrap gap-2">
                    {lab.tags.map((tag, ti) => <li key={ti} data-index={ti} className="font-mono text-[11px] tracking-wider text-primary border border-border px-2 py-1 transition-colors duration-0 group-hover:text-foreground group-hover:border-primary">
                        {tag}
                      </li>)}
                  </ul>

                  {}
                  <div className="mt-auto overflow-hidden pt-6">
                    <Link to="/TheGearPage" className="font-mono text-xs tracking-widest uppercase flex items-center gap-2 translate-y-8 opacity-0 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100" style={{
                  color: `var(${lab.accentVar})`
                }}>
                      <span>FULL SPECS</span>
                      <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>

                {}
                <span aria-hidden="true" className="pointer-events-none absolute top-0 left-0 h-3 w-3 border-t border-l opacity-0 transition-opacity duration-0 group-hover:opacity-100" style={{
              borderColor: `var(${lab.accentVar})`
            }} />
                <span aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 h-3 w-3 border-b border-r opacity-0 transition-opacity duration-0 group-hover:opacity-100" style={{
              borderColor: `var(${lab.accentVar})`
            }} />
              </article>;
        })}
        </div>

        {}
        <div className="mt-16 flex justify-center">
          <Button asChild variant="ghost" className="font-mono text-xs sm:text-sm tracking-[0.2em] uppercase text-foreground hover:bg-transparent hover:text-primary group/btn px-0">
            <Link to="/TheGearPage" className="inline-flex items-center gap-3">
              <span className="h-px w-8 bg-border transition-all duration-500 group-hover/btn:w-16" style={{
              backgroundColor: "var(--chart-1)"
            }} />
              <span>EXPLORE THE FULL INVENTORY</span>
              <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" strokeWidth={1.5} />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}